/**
 * Supabase client configuration.
 *
 * Replace with actual project credentials from your Supabase dashboard.
 * Run `npx supabase gen types --lang=typescript` after creating tables
 * to generate full Database types.
 */

import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  if (import.meta.env.PROD) {
    throw new Error(
      'Supabase credentials are required in production. ' +
        'Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
    )
  }
  console.warn(
    'Supabase credentials not found. Auth and cloud sync will be disabled. ' +
      'Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local'
  )
}

/**
 * Minimal Database interface for type safety.
 * Extend this after running `supabase gen types`.
 */
export interface Database {
  public: {
    Tables: {
      plans: {
        Row: {
          id: string
          user_id: string
          household_id: string | null
          name: string
          data: Record<string, unknown>
          is_shared: boolean
          created_at: string
          updated_at: string
        }
        Insert: Omit<Database['public']['Tables']['plans']['Row'], 'created_at'> & { id?: string }
        Update: Partial<Database['public']['Tables']['plans']['Insert']>
      }
      saved_configs: {
        Row: {
          id: string
          user_id: string
          household_id: string | null
          name: string
          config: Record<string, unknown>
          is_active: boolean
          is_shared: boolean
          created_at: string
        }
        Insert: Omit<Database['public']['Tables']['saved_configs']['Row'], 'created_at'> & { id?: string }
        Update: Partial<Database['public']['Tables']['saved_configs']['Insert']>
      }
      user_profiles: {
        Row: {
          id: string
          display_name: string | null
          avatar_url: string | null
          preferences: Record<string, unknown>
          subscription_tier: 'free' | 'pro' | 'family'
          subscription_status: 'active' | 'cancelled' | 'past_due'
          stripe_customer_id: string | null
          created_at: string
          updated_at: string
        }
        Insert: Partial<Database['public']['Tables']['user_profiles']['Row']> & { id: string }
        Update: Partial<Database['public']['Tables']['user_profiles']['Row']>
      }
      households: {
        Row: {
          id: string
          name: string
          owner_id: string
          invite_code: string | null
          max_members: number
          created_at: string
        }
        Insert: Omit<Database['public']['Tables']['households']['Row'], 'id' | 'created_at' | 'max_members' | 'invite_code'> & { id?: string, max_members?: number, invite_code?: string | null }
        Update: Partial<Database['public']['Tables']['households']['Insert']>
      }
      household_members: {
        Row: {
          id: string
          household_id: string
          user_id: string
          role: 'owner' | 'admin' | 'member'
          joined_at: string
        }
        Insert: Omit<Database['public']['Tables']['household_members']['Row'], 'id' | 'joined_at'> & { id?: string }
        Update: Partial<Database['public']['Tables']['household_members']['Insert']>
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      join_household: {
        Args: { p_invite_code: string }
        Returns: Record<string, unknown>
      }
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
}

export const supabase = createClient<Database>(
  supabaseUrl || 'http://localhost:54321',
  supabaseAnonKey || 'dummy-key'
)

export type SupabaseClient = typeof supabase
